/**
 * ZeroBounce Real-Time Email Validation
 * Validates email inputs on any form marked with [data-zb-validate]
 * before it is submitted to Shopify.
 *
 * Configuration is injected by theme.liquid via window.ZeroBounceConfig:
 *   { apiKey: '...', enabled: true, blockMode: 'invalid_spam_abuse' }
 */
(function () {
  'use strict';

  var cfg = window.ZeroBounceConfig || {};
  if (!cfg.enabled || !cfg.apiKey) return;

  var API_KEY = cfg.apiKey;
  var BLOCK_MODE = cfg.blockMode || 'invalid_spam_abuse';

  // Statuses considered invalid — always blocked
  var ALWAYS_BLOCK = ['invalid'];

  // Extra statuses blocked depending on theme setting
  var SPAM_ABUSE = ['spamtrap', 'abuse'];
  var DO_NOT_MAIL = ['do_not_mail'];

  function getBlockedStatuses() {
    var blocked = ALWAYS_BLOCK.slice();
    if (BLOCK_MODE === 'invalid_spam_abuse' || BLOCK_MODE === 'invalid_spam_abuse_donotmail') {
      blocked = blocked.concat(SPAM_ABUSE);
    }
    if (BLOCK_MODE === 'invalid_spam_abuse_donotmail') {
      blocked = blocked.concat(DO_NOT_MAIL);
    }
    return blocked;
  }

  // Session-level cache to avoid repeat API calls for the same address
  var cache = {};

  function validateEmail(email) {
    var normalised = email.toLowerCase().trim();
    if (cache[normalised] !== undefined) {
      return Promise.resolve(cache[normalised]);
    }
    var url =
      'https://api.zerobounce.net/v2/validate' +
      '?api_key=' + encodeURIComponent(API_KEY) +
      '&email=' + encodeURIComponent(normalised) +
      '&ip_address=';

    return fetch(url)
      .then(function (res) {
        if (!res.ok) return null;
        return res.json();
      })
      .then(function (data) {
        cache[normalised] = data;
        return data;
      })
      .catch(function () {
        // Network failure — fail open so legitimate users are not blocked
        return null;
      });
  }

  // ── UI helpers ────────────────────────────────────────────────────────────

  function showError(input, html) {
    clearError(input);
    input.classList.add('has-errors');
    var msg = document.createElement('p');
    msg.className = 'form__message form__message--error zb-email-error';
    msg.setAttribute('role', 'alert');
    msg.setAttribute('aria-live', 'assertive');
    msg.innerHTML =
      '<span class="visually-hidden">Error: </span>' + html;
    // Insert immediately after the input so the message always appears
    // directly below the field, regardless of surrounding layout.
    input.insertAdjacentElement('afterend', msg);
    // Mark the form as invalid and visually disable the submit button.
    // data-zb-invalid is also read by the overridden form.submit() method
    // to block programmatic submissions from third-party scripts (e.g. Klaviyo).
    var form = input.form || input.closest('form');
    if (form) {
      form.setAttribute('data-zb-invalid', '');
      var btn = getSubmitButton(form);
      if (btn) btn.setAttribute('data-zb-error-disabled', '');
    }
  }

  function clearError(input) {
    input.classList.remove('has-errors');
    // Primary location: right after the input
    var next = input.nextElementSibling;
    if (next && next.classList.contains('zb-email-error')) {
      next.remove();
    }
    // Fallback: anywhere inside the closest [data-zb-validate] ancestor
    var root = input.closest('[data-zb-validate]') || input.parentElement;
    var stale = root.querySelector('.zb-email-error');
    if (stale) stale.remove();
    // Clear the invalid flag and re-enable the submit button.
    var form = input.form || input.closest('form');
    if (form) {
      form.removeAttribute('data-zb-invalid');
      var btn = getSubmitButton(form);
      if (btn) btn.removeAttribute('data-zb-error-disabled');
    }
  }

  function setInputBusy(input, busy) {
    if (busy) {
      input.setAttribute('aria-busy', 'true');
      input.setAttribute('disabled', '');
      input.setAttribute('data-zb-validating', '');
    } else {
      input.removeAttribute('aria-busy');
      input.removeAttribute('disabled');
      input.removeAttribute('data-zb-validating');
    }
  }

  function getSubmitButton(form) {
    return form.querySelector('[type="submit"]');
  }

  function setSubmitBusy(form, busy) {
    var btn = getSubmitButton(form);
    if (!btn) return;
    if (busy) {
      btn.setAttribute('disabled', '');
      btn.setAttribute('data-zb-original-text', btn.textContent);
      if (btn.tagName === 'BUTTON') btn.textContent = 'Validating\u2026';
    } else {
      btn.removeAttribute('disabled');
      var orig = btn.getAttribute('data-zb-original-text');
      if (orig && btn.tagName === 'BUTTON') btn.textContent = orig;
      btn.removeAttribute('data-zb-original-text');
    }
  }

  // ── Validation logic ──────────────────────────────────────────────────────

  function buildErrorMessage(result) {
    var status = result.status;
    var sub = result.sub_status || '';
    var suggestion = result.did_you_mean;

    if (suggestion) {
      return (
        'That email address looks incorrect. Did you mean <strong>' +
        escapeHtml(suggestion) +
        '</strong>?'
      );
    }
    if (status === 'invalid') {
      if (sub === 'failed_syntax_check' || sub === 'possible_typo') {
        return 'Please check the spelling of your email address.';
      }
      if (sub === 'mailbox_not_found' || sub === 'no_dns_entries') {
        return 'This email address does not exist. Please try a different one.';
      }
      return 'Please enter a valid email address.';
    }
    if (status === 'spamtrap' || status === 'abuse') {
      return 'This email address cannot be accepted. Please use a different one.';
    }
    if (status === 'do_not_mail') {
      return 'This email address cannot be used for subscriptions. Please try a different one.';
    }
    return 'Please enter a valid email address.';
  }

  function escapeHtml(str) {
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  // ── Form submit handler ───────────────────────────────────────────────────

  function handleSubmit(event) {
    var form = event.currentTarget;
    var input = form.querySelector('input[type="email"]');
    if (!input || !input.value.trim()) {
      event.preventDefault();
      return;
    }

    // Block immediately when a ZeroBounce error is visible.
    // stopImmediatePropagation prevents third-party scripts (e.g. Klaviyo)
    // from seeing this event and triggering their own form submission.
    if (form.hasAttribute('data-zb-invalid') || form.querySelector('.zb-email-error')) {
      event.preventDefault();
      event.stopImmediatePropagation();
      input.focus();
      return;
    }

    var email = input.value.trim();
    event.preventDefault();
    event.stopImmediatePropagation();

    clearError(input);
    setInputBusy(input, true);
    setSubmitBusy(form, true);

    validateEmail(email).then(function (result) {
      setInputBusy(input, false);
      setSubmitBusy(form, false);

      if (!result || result.error) {
        // API error or no credits — fail open
        form.submit();
        return;
      }

      var blocked = getBlockedStatuses();
      if (blocked.indexOf(result.status) !== -1) {
        showError(input, buildErrorMessage(result));
        input.focus();
      } else {
        form.submit();
      }
    });
  }

  // ── Blur handler (early feedback) ─────────────────────────────────────────

  function handleBlur(event) {
    var input = event.currentTarget;
    var email = input.value.trim();
    if (!email || input.getAttribute('data-zb-validating')) return;

    validateEmail(email).then(function (result) {
      if (!result || result.error) return;
      var blocked = getBlockedStatuses();
      if (blocked.indexOf(result.status) !== -1) {
        showError(input, buildErrorMessage(result));
      } else {
        clearError(input);
      }
    });
  }

  // ── Attachment ────────────────────────────────────────────────────────────

  function attachToForm(form) {
    if (form.dataset.zbAttached) return;
    form.dataset.zbAttached = 'true';

    // Register in the CAPTURE phase so our handler runs before any third-party
    // bubble-phase listeners (e.g. Klaviyo). Combined with stopImmediatePropagation
    // this prevents Klaviyo from ever seeing the event when we need to block.
    form.addEventListener('submit', handleSubmit, true);

    // Override the form's own .submit() method so that programmatic calls made
    // by third-party scripts (e.g. Klaviyo calling form.submit() after its own
    // AJAX step) are also blocked while a ZeroBounce error is visible.
    var _nativeSubmit = HTMLFormElement.prototype.submit;
    form.submit = function zbSubmit() {
      if (form.hasAttribute('data-zb-invalid')) return;
      _nativeSubmit.call(form);
    };

    var input = form.querySelector('input[type="email"]');
    if (input) {
      input.addEventListener('blur', handleBlur);
      input.addEventListener('input', function () {
        clearError(input);
      });
    }
  }

  function attachToContainer(container) {
    var form = container.tagName === 'FORM' ? container : container.querySelector('form');
    if (form) attachToForm(form);
  }

  function init() {
    document.querySelectorAll('[data-zb-validate]').forEach(attachToContainer);
  }

  // Watch for dynamically injected forms (e.g. popup newsletter widgets)
  var observer = new MutationObserver(function (mutations) {
    mutations.forEach(function (mutation) {
      mutation.addedNodes.forEach(function (node) {
        if (node.nodeType !== 1) return;
        if (node.matches && node.matches('[data-zb-validate]')) {
          attachToContainer(node);
        }
        if (node.querySelectorAll) {
          node.querySelectorAll('[data-zb-validate]').forEach(attachToContainer);
        }
      });
    });
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init();
      observer.observe(document.body, { childList: true, subtree: true });
    });
  } else {
    init();
    observer.observe(document.body, { childList: true, subtree: true });
  }
})();
